import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { Tabs, Skeleton, message } from 'antd';

import LeaseOverview from './LeaseOverview';
import ApplicationHeader from './ApplicationHeader';
import Overview from './application_overview/Overview';
import Review from './application_overview/Review';
import BaseApplication from './application_overview/BaseApplication';
import Notes from './application_overview/Notes';
import History from './application_overview/History';
import Applicants from './application_overview/Applicants';
import ApplicantSelector from './application_overview/applicants/ApplicantSelector';
import { getTranslatedString } from 'helpers/i18n';

import { featureEnabled } from 'helpers/ToggleBot';
import {
  atLeastOneApplicantSubmitted,
  getDefaultTabSelected,
  getSubtitle,
  canCurrentAgentViewApplication,
} from './common/helpers';
import { useApplication } from './common/queries/useApplication';

export const Draft = 'draft';
export const Pending = 'pending';

const StyledContainer = styled.div`
  padding: 0 24px;
`;

const StyledTabPane = styled(Tabs.TabPane)`
  display: flex;
  gap: 16px;

  .ant-card,
  > * {
    flex: auto;
    max-width: 100%;
  }
`;

const translate = (key) =>
  getTranslatedString(`landlordPortal.applicationManagement.applicationOverview.${key}`);

export const CurrentApplicationContext = React.createContext({});

const ApplicationOverview = ({ router, routeParams, isAdmin }) => {
  const [showHistoryTab, setShowHistoryTab] = useState(false);
  const [selectedApplicantId, setSelectedApplicantId] = useState(undefined);

  const { isLoading, data: currentApplication } = useApplication(routeParams?.applicationID);
  const showReviewTab = atLeastOneApplicantSubmitted(currentApplication?.applicants || []);

  useEffect(() => {
    setShowHistoryTab(featureEnabled('history-tab') && isAdmin);
  }, [routeParams]);

  useEffect(() => {
    if (currentApplication?.applicants) {
      setSelectedApplicantId(currentApplication.applicants[0].custom_id);
    }
  }, [currentApplication?.applicants]);

  useEffect(() => {
    if (!canCurrentAgentViewApplication(currentApplication)) {
      router.push('/companies/applications');
      setTimeout(() => {
        message.error(translate('lackOfPermissionsRedirect'));
      }, 1500);
    }
  }, [currentApplication?.can_approve, currentApplication?.can_review]);

  // There are some global styles that clashes with Application Management styles. Scoping any style with .application-management should work for now.
  // To add said scope to the highest parent element possible (ie: the <body> tag) we could use a useEffect hook that adds the scoping class after mount, and removes it after unmount.
  useEffect(() => {
    document.body.classList.add('application-management');

    return () => document.body.classList.remove('application-management');
  }, []);

  if (isLoading || !currentApplication) {
    return (
      <StyledContainer>
        <Skeleton
          paragraph={{
            rows: 1,
          }}
        />
      </StyledContainer>
    );
  }

  const subTitle = getSubtitle(currentApplication);

  const tabList = [
    {
      key: 'overview',
      title: translate('overview'),
      content: <Overview />,
    },
    showReviewTab && {
      key: 'review',
      title: translate('review'),
      content: <Review />,
    },
    {
      key: 'baseApplication',
      title: translate('baseApplication'),
      content: <BaseApplication />,
    },
    {
      key: 'applicants',
      title: translate('applicants'),
      content: <Applicants selectedApplicantId={selectedApplicantId} />,
      side_content: (
        <ApplicantSelector
          selectedApplicantId={selectedApplicantId}
          setSelectedApplicantId={setSelectedApplicantId}
        />
      ),
    },
    {
      key: 'notes',
      title: translate('notes'),
      content: <Notes applicationId={routeParams.applicationID} />,
    },
    showHistoryTab && {
      key: 'history',
      title: translate('history'),
      content: <History />,
    },
  ].filter(Boolean);

  const defaultTabSelected = getDefaultTabSelected(tabList, routeParams.tab);

  const onChangeTab = (activeTabKey) => {
    router.push(`/companies/applications/${routeParams?.applicationID}/${activeTabKey}`);
  };

  return (
    <div className="body" role="main">
      <CurrentApplicationContext.Provider value={currentApplication}>
        {currentApplication?.lease?.lifecycle ? (
          <LeaseOverview routeParams={routeParams} onChangeTab={onChangeTab} />
        ) : (
          <>
            <ApplicationHeader subTitle={subTitle} />
            <StyledContainer>
              <Tabs defaultActiveKey={defaultTabSelected} onChange={onChangeTab}>
                {tabList.map((tab) => (
                  <React.Fragment key={tab.key}>
                    <StyledTabPane tab={tab.title} key={tab.key}>
                      {tab.content}
                      {tab.side_content}
                    </StyledTabPane>
                  </React.Fragment>
                ))}
              </Tabs>
            </StyledContainer>
          </>
        )}
      </CurrentApplicationContext.Provider>
    </div>
  );
};

const mapStoreToProps = (store) => ({
  isAdmin: store.userTypes.admin,
});

export default withRouter(connect(mapStoreToProps, null)(ApplicationOverview));
